import React, { useState } from 'react';
import {
  Input,
  Typography,
  FormControl,
  FormLabel,
  Button,
  Box,
  Sheet,
  Alert
} from '@mui/joy';
import WarningIcon from '@mui/icons-material/Warning';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useNavigate, useLocation } from 'react-router-dom';
import { signIn } from '../../../apiconfig/api';
import { useSession } from '../../../context/SessionContext';
import { getCurrentUser } from '../../../apiconfig/api';

interface FormValues {
  email: string;
  password: string;
}

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector((state: RootState) => state.auth.loading);
  const { setSessionEmployerId } = useSession();
  
  // Get redirect URL from query parameters
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formik = useFormik<FormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      password: Yup.string()
        .required('Password is required'),
    }),
    onSubmit: async (values) => {
      try {
        setErrorMessage('');
        setIsLoading(true);

        const response = await signIn(values.email, values.password);

        // Store the token in localStorage
        if (response.data.token) {
          localStorage.setItem('session_token', response.data.token);
        }

        // Set sessionEmployerId after login
        try {
          const userResp = await getCurrentUser();
          const user = userResp.data.user;
          // If user has an employers array, use the first one; else fallback to user id
          if (user.employers && Array.isArray(user.employers) && user.employers.length > 0) {
            setSessionEmployerId(user.employers[0].uuid);
          }
        } catch (e) {
          // fallback: clear sessionEmployerId
          setSessionEmployerId(null);
        }

        // Check for redirect path from localStorage (set by session expired modal)
        const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
        
        // Navigate to redirect URL, stored redirect path, or dashboard
        const finalRedirectUrl = redirectUrl || redirectAfterLogin || '/hiring/positions';
        
        // Clear the stored redirect path
        if (redirectAfterLogin) {
          localStorage.removeItem('redirectAfterLogin');
        }
        
        navigate(finalRedirectUrl);

      } catch (error: any) {
        console.error('Error during sign-in:', error);
        
        // Extract error message from backend response
        let errorMsg = 'Sign-in failed. Please try again.';
        
        if (error.message) {
          // If it's already a string message from the backend
          errorMsg = error.message;
        } else if (error.response?.data?.message) {
          // If it's an axios-style error with response data
          errorMsg = error.response.data.message;
        } else if (typeof error === 'string') {
          // If error is directly a string
          errorMsg = error;
        }
        
        setErrorMessage(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  });

  const { values, handleChange, handleSubmit, touched, errors, handleBlur } = formik;

  const inputStyles = {
    height: '56px',
    backgroundColor: '#ECEFF3',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.125rem',
    color: '#1A1B1D',
    '--Input-focusedHighlight': 'transparent !important',
    '&::placeholder': {
      color: '#7B7F85 !important',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: '#ECEFF3',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: '#ECEFF3',
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f4f4',
        padding: '20px',
      }}
    >
      <Sheet
        sx={{
          width: '100%',
          maxWidth: '480px',
          padding: '40px',
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <form onSubmit={handleSubmit}>
          <Typography 
            level="h1" 
            marginBottom="32px" 
            fontSize="28px" 
            sx={{ 
              fontWeight: 600, 
              color: '#1A1B1D',
              textAlign: 'center'
            }}
          >
            Welcome Back!
          </Typography>

          {errorMessage && (
            <Alert 
              color="danger" 
              startDecorator={<WarningIcon />}
              sx={{ 
                marginBottom: '24px',
                borderRadius: '8px'
              }}
            >
              {errorMessage}
            </Alert>
          )}

          <FormControl error={touched.email && Boolean(errors.email)} sx={{ marginBottom: '24px' }}>
            <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
              Email Address
            </FormLabel>
            <Input
              type="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="your.email@example.com"
              autoComplete="email"
              sx={inputStyles}
            />
            {touched.email && errors.email && (
              <Typography level="body-sm" color="danger" marginTop="5px">
                {errors.email}
              </Typography>
            )}
          </FormControl>

          <FormControl error={touched.password && Boolean(errors.password)} sx={{ marginBottom: '32px' }}>
            <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
              Password
            </FormLabel>
            <Input
              type="password"
              name="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter your password"
              autoComplete="current-password"
              sx={inputStyles}
            />
            {touched.password && errors.password && (
              <Typography level="body-sm" color="danger" marginTop="5px">
                {errors.password}
              </Typography>
            )}
          </FormControl>

          <Button
            variant="solid"
            fullWidth
            type="submit"
            loading={isLoading}
            sx={{
              height: '56px',
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '24px',
            }}
          >
            Sign In
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              level="body-sm" 
              sx={{ 
                color: '#7B7F85', 
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              Don't have an account?<br/>
              <Typography
                component="span"
                sx={{
                  color: '#2e2d7e',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => navigate(`/apply`)}
              >
                Browse Jobs and Apply
              </Typography>
                {' '}or{' '}
              <Typography
                component="span"
                sx={{
                  color: '#2e2d7e',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => navigate(`/hiring/post-job-workflow`)}
              >
                Post a Job
              </Typography>
            </Typography>
          </Box>
        </form>
      </Sheet>
    </Box>
  );
};

export default SignInPage; 